import { useState, useEffect, useRef } from "react";
import { X, Cookie, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import LocaleLink from '@/components/LocaleLink';
import { useTranslation } from "react-i18next";
import { pushConsentUpdate, replayStoredConsent } from "@/lib/consentMode";
import { applyGpc, hasExplicitConsentChoice } from "@/lib/globalPrivacyControl";

const readStoredConsent = (): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem('cookie-consent');
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

const ConsentBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Cookie consent preferences including doNotSell
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    functional: false,
    analytics: false,
    marketing: false,
    doNotSell: false
  });

  // A /do-not-sell write is not a banner choice, so this UI still shows and
  // the toggle starts false. Until the visitor edits that toggle, the stored
  // sale opt-out is the choice Accept, Essential, and dismiss must keep.
  const saleChoiceEdited = useRef(false);

  const resolveDoNotSell = (stored: Record<string, unknown>) => {
    if (saleChoiceEdited.current) return preferences.doNotSell;
    return preferences.doNotSell || stored.doNotSell === true;
  };

  useEffect(() => {
    /*
     * Global Privacy Control first. It is a legally recognised opt-out, so it
     * takes effect on the first page view rather than waiting for a banner
     * click — the browser has already answered the question. applyGpc writes
     * the floor into storage and returns the record to push; it only ever turns
     * marketing off, so an explicit later opt-in through the banner survives.
     *
     * The privacy policy has promised this since before it was implemented.
     */
    const gpc = applyGpc();
    if (gpc) {
      pushConsentUpdate(gpc);
    } else {
      // Replay stored consent into Google Consent Mode v2 on every load.
      replayStoredConsent();
    }

    // GPC and /do-not-sell both store a sale opt-out without a banner choice,
    // so this UI still has to appear for the Europe analytics grant. Copy the
    // stored flag into banner state either way. Leaving the default false makes
    // Accept, Essential, and dismiss overwrite the opt-out and the usprivacy cookie.
    const storedOptOut = gpc?.doNotSell === true || readStoredConsent().doNotSell === true;
    if (storedOptOut) {
      setPreferences((prev) => ({ ...prev, marketing: false, doNotSell: true }));
    }

    if (!hasExplicitConsentChoice()) {
      setShowBanner(true);
    }

    // The page can opt out or back in while this banner is already open.
    const onConsentUpdated = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      if (!detail || typeof detail.doNotSell !== 'boolean') return;
      const optedOut = detail.doNotSell === true;
      setPreferences((prev) => ({
        ...prev,
        doNotSell: optedOut,
        marketing: optedOut ? false : prev.marketing,
      }));
    };
    window.addEventListener('consentUpdated', onConsentUpdated);
    return () => window.removeEventListener('consentUpdated', onConsentUpdated);
  }, []);

  const setUSPrivacyCookie = (doNotSell: boolean) => {
    // Set IAB US Privacy (usprivacy) cookie
    const uspString = doNotSell ? '1YYN' : '1YNN'; // 1 = version, Y = has notice, Y/N = opt-out of sale, N = no LSPA
    document.cookie = `usprivacy=${uspString}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
  };

  const acceptAll = () => {
    const stored = readStoredConsent();
    const doNotSell = resolveDoNotSell(stored);
    const allAccepted = {
      ...stored,
      essential: true,
      functional: true,
      analytics: true,
      marketing: !doNotSell,
      doNotSell,
      timestamp: Date.now(),
      bannerChosen: true,
    };
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setUSPrivacyCookie(doNotSell);
    pushConsentUpdate(allAccepted);
    setShowBanner(false);

    window.dispatchEvent(new CustomEvent('consentUpdated', { detail: allAccepted }));
  };

  const acceptEssential = () => {
    const stored = readStoredConsent();
    const doNotSell = resolveDoNotSell(stored);
    const essentialOnly = {
      ...stored,
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      doNotSell,
      timestamp: Date.now(),
      bannerChosen: true,
    };
    localStorage.setItem('cookie-consent', JSON.stringify(essentialOnly));
    setUSPrivacyCookie(doNotSell);
    pushConsentUpdate(essentialOnly);
    setShowBanner(false);

    window.dispatchEvent(new CustomEvent('consentUpdated', { detail: essentialOnly }));
  };

  const savePreferences = () => {
    const stored = readStoredConsent();
    const doNotSell = resolveDoNotSell(stored);
    const consent = {
      ...stored,
      ...preferences,
      marketing: preferences.marketing && !doNotSell,
      doNotSell,
      timestamp: Date.now(),
      bannerChosen: true,
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    setUSPrivacyCookie(doNotSell);
    pushConsentUpdate(consent);
    setShowBanner(false);
    setShowPreferences(false);

    window.dispatchEvent(new CustomEvent('consentUpdated', { detail: consent }));
  };

  const handlePreferenceChange = (type: keyof typeof preferences) => {
    if (type === 'essential') return; // Essential cookies can't be disabled
    
    if (type === 'doNotSell') saleChoiceEdited.current = true;

    setPreferences(prev => {
      const newPrefs = { ...prev, [type]: !prev[type] };
      
      // If enabling doNotSell, disable marketing
      if (type === 'doNotSell' && !prev.doNotSell) {
        newPrefs.marketing = false;
      }
      
      return newPrefs;
    });
  };

  if (!showBanner) return null;

  return (
    <div data-testid="consent-banner" className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-3">
      <Card className="bg-background border shadow-lg max-w-4xl mx-auto">
        {!showPreferences ? (
          <div className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Cookie className="w-5 h-5 text-primary flex-shrink-0 hidden sm:block" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">
                  {t('consent.description')}{" "}
                  <LocaleLink to="/cookie-policy" className="text-primary hover:underline">
                    {t('consent.cookiePolicy')}
                  </LocaleLink>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <Button onClick={acceptAll} size="sm" className="flex-1 sm:flex-initial">
                  {t('consent.acceptAll')}
                </Button>
                <Button variant="outline" size="sm" onClick={acceptEssential} className="flex-1 sm:flex-initial">
                  {t('consent.essentialOnly')}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowPreferences(true)}
                  className="px-2"
                  aria-label="Cookie preferences"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={acceptEssential}
                  className="px-2"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Settings className="w-4 h-4" />
                {t('consent.preferencesTitle')}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreferences(false)}
                className="px-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between p-2 bg-muted rounded-lg">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium">{t('consent.essentialCookies')}</h4>
                  <p className="text-xs text-muted-foreground">{t('consent.essentialCookiesDescription')}</p>
                </div>
                <div className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium ml-2">
                  {t('consent.alwaysActive')}
                </div>
              </div>
              
              <div className="flex items-center justify-between p-2 border rounded-lg">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium">{t('consent.functionalCookies')}</h4>
                  <p className="text-xs text-muted-foreground">{t('consent.functionalCookiesDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-2">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={() => handlePreferenceChange('functional')}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-2 border rounded-lg">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium">{t('consent.analyticsCookies')}</h4>
                  <p className="text-xs text-muted-foreground">{t('consent.analyticsCookiesDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-2">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => handlePreferenceChange('analytics')}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-2 border rounded-lg">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium">{t('consent.marketingCookies')}</h4>
                  <p className="text-xs text-muted-foreground">{t('consent.marketingCookiesDescription')}</p>
                </div>
                <label className={`relative inline-flex items-center cursor-pointer ml-2 ${preferences.doNotSell ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => handlePreferenceChange('marketing')}
                    disabled={preferences.doNotSell}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary peer-disabled:cursor-not-allowed"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-amber-800">{t('consent.doNotSell')}</h4>
                  <p className="text-xs text-amber-700">{t('consent.doNotSellDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-2">
                  <input
                    type="checkbox"
                    checked={preferences.doNotSell}
                    onChange={() => handlePreferenceChange('doNotSell')}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={savePreferences} size="sm" className="flex-1">
                {t('consent.savePreferences')}
              </Button>
              <Button variant="outline" size="sm" onClick={acceptAll} className="flex-1">
                {t('consent.acceptAll')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ConsentBanner;
